import { makeAutoObservable } from "mobx"
import { apiClient } from "api/trpc/trpc-client"
import React, { createRef } from "react"

interface GapMindsetComponentStoreProps {
   attributes: Record<string, string> | undefined
   updateMdastNode: (node: any) => void
}

export default class GapMindsetComponentStore {
   sp
   id = ''
   orgId = ''
   downloadUrl: string | null = null
   loading = true
   error = false
   width: number | null = null
   height: number | null = null
   hovered = false
   imgRef = createRef<HTMLImageElement>()
   containerRef = createRef<HTMLDivElement>()

   constructor(sp: GapMindsetComponentStoreProps) {
      makeAutoObservable(this, {
         imgRef: false,
         containerRef: false,
      })
      this.sp = sp
      this.id = sp.attributes?.id || ''
      this.orgId = sp.attributes?.orgId || ''
      const parsedWidth = Number(sp.attributes?.width)
      this.width = parsedWidth > 0 ? parsedWidth : null
      const parsedHeight = Number(sp.attributes?.height)
      this.height = parsedHeight > 0 ? parsedHeight : null
   }

   getDownloadUrl = async () => {
      if (!this.id || !this.orgId) {
         this.error = true
         this.loading = false
         return
      }
      try {
         const result = await apiClient.mindset.getMindsetImageUrlById.query({ orgId: this.orgId, id: this.id })
         this.downloadUrl = result?.url ?? null
         if (!this.downloadUrl) {
            this.error = true
         }
      } catch {
         this.error = true
      } finally {
         this.loading = false
      }
   }

   handleImageError = () => {
      this.error = true
   }

   handleMouseEnter = () => {
      this.hovered = true
   }

   handleMouseLeave = () => {
      this.hovered = false
   }

   handlePointerDown = (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()

      const img = this.imgRef.current
      const container = this.containerRef.current
      if (!img || !container) return

      const handleIndex = Number((e.currentTarget as HTMLElement).dataset.handleIndex)
      const startX = e.clientX
      const startWidth = img.getBoundingClientRect().width
      const aspectRatio = img.naturalWidth / img.naturalHeight
      const maxWidth = container.getBoundingClientRect().width
      const isLeft = handleIndex === 0 || handleIndex === 2

      document.body.style.userSelect = 'none'

      const onPointerMove = (moveEvent: PointerEvent) => {
         const dx = moveEvent.clientX - startX
         const widthDelta = isLeft ? -dx : dx
         const newWidth = Math.max(100, Math.min(maxWidth, startWidth + widthDelta))
         img.style.width = `${newWidth}px`
         img.style.height = `${newWidth / aspectRatio}px`
      }

      const onPointerUp = () => {
         document.removeEventListener('pointermove', onPointerMove)
         document.removeEventListener('pointerup', onPointerUp)
         document.body.style.userSelect = ''

         const rect = img.getBoundingClientRect()
         this.handleResizeEnd(rect.width, rect.height)
      }

      document.addEventListener('pointermove', onPointerMove)
      document.addEventListener('pointerup', onPointerUp)
   }

   handleResizeEnd = (newWidth: number, newHeight: number) => {
      this.width = newWidth
      this.height = newHeight
      this.sp.updateMdastNode({
         attributes: {
            ...this.sp.attributes,
            width: String(Math.round(newWidth)),
            height: String(Math.round(newHeight)),
         },
      })
   }
}
